import Vue from 'vue'
import VueRouter from 'vue-router'
import { registerGuards } from './guards'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/views/home/Landing.vue'),
    meta: { title: 'Home', requiresAuth: false, standaloneLayout: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { title: 'Login', requiresAuth: false, standaloneLayout: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { title: 'Register', requiresAuth: false, standaloneLayout: true },
  },
  {
    path: '/auth/google/callback',
    name: 'GoogleCallback',
    component: () => import('@/views/auth/GoogleCallback.vue'),
    meta: { title: 'Google Sign In', requiresAuth: false, standaloneLayout: true },
  },
  {
    path: '/terms',
    name: 'LegalTerms',
    component: () => import('@/views/legal/LegalPage.vue'),
    props: { page: 'terms' },
    meta: { title: 'Terms of Service', requiresAuth: false, standaloneLayout: true, allowLoggedIn: true },
  },
  {
    path: '/privacy',
    name: 'LegalPrivacy',
    component: () => import('@/views/legal/LegalPage.vue'),
    props: { page: 'privacy' },
    meta: { title: 'Privacy Policy', requiresAuth: false, standaloneLayout: true, allowLoggedIn: true },
  },
  {
    path: '/cookies',
    name: 'LegalCookies',
    component: () => import('@/views/legal/LegalPage.vue'),
    props: { page: 'cookies' },
    meta: { title: 'Cookie Policy', requiresAuth: false, standaloneLayout: true, allowLoggedIn: true },
  },
  {
    path: '/refund',
    name: 'LegalRefund',
    component: () => import('@/views/legal/LegalPage.vue'),
    props: { page: 'refund' },
    meta: { title: 'Refund Policy', requiresAuth: false, standaloneLayout: true, allowLoggedIn: true },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/home/Dashboard.vue'),
    meta: { title: 'Dashboard', requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/profile/Profile.vue'),
    meta: { title: 'Profile', requiresAuth: true },
    beforeEnter(to, _from, next) {
      if (to.hash === '#billing') {
        next({ path: '/billing', query: to.query, replace: true })
        return
      }
      next()
    },
  },
  {
    path: '/social/accounts',
    name: 'SocialAccounts',
    component: () => import('@/views/social/Accounts.vue'),
    meta: { title: 'Social Accounts', requiresAuth: true },
  },
  {
    path: '/posts',
    name: 'Posts',
    component: () => import('@/views/posts/PostList.vue'),
    meta: { title: 'Posts', requiresAuth: true },
  },
  {
    path: '/posts/create',
    name: 'CreatePost',
    component: () => import('@/views/posts/PostComposer.vue'),
    meta: { title: 'Create Post', requiresAuth: true },
  },
  {
    path: '/posts/calendar',
    name: 'Calendar',
    component: () => import('@/views/posts/Calendar.vue'),
    meta: { title: 'Calendar', requiresAuth: true },
  },
  {
    path: '/posts/approval',
    name: 'ApprovalQueue',
    component: () => import('@/views/posts/ApprovalQueue.vue'),
    meta: { title: 'Approval Queue', requiresAuth: true },
  },
  {
    path: '/posts/:id/edit',
    name: 'EditPost',
    component: () => import('@/views/posts/PostComposer.vue'),
    meta: { title: 'Edit Post', requiresAuth: true },
  },
  {
    path: '/posts/:id',
    name: 'PostDetail',
    component: () => import('@/views/posts/PostDetail.vue'),
    meta: { title: 'Post Detail', requiresAuth: true },
  },
  {
    path: '/ai/assistant',
    name: 'AIAssistant',
    component: () => import('@/views/ai/AIAssistant.vue'),
    meta: { title: 'AI Assistant', requiresAuth: true },
  },
  {
    path: '/ai/studio',
    name: 'DesignStudio',
    component: () => import('@/views/ai/DesignHub.vue'),
    meta: { title: 'Design Studio', requiresAuth: true },
  },
  {
    path: '/ai/studio/new',
    name: 'DesignStudioNew',
    component: () => import('@/views/ai/DesignStudio.vue'),
    meta: { title: 'New Design', requiresAuth: true },
  },
  {
    path: '/docs/studio-api',
    redirect: { path: '/ai/studio/api' },
  },
  {
    path: '/ai/studio/api',
    name: 'StudioApiDocs',
    component: () => import('@/views/ai/StudioApiDocs.vue'),
    meta: {
      title: 'Studio API',
      requiresAuth: false,
      standaloneLayout: true,
      allowLoggedIn: true,
      publicDocs: true,
    },
  },
  {
    path: '/ai/studio/playground',
    name: 'StudioApiPlayground',
    component: () => import('@/views/ai/StudioApiPlayground.vue'),
    meta: { title: 'API Playground', requiresAuth: true },
  },
  {
    path: '/ai/studio/:id',
    name: 'DesignStudioEdit',
    component: () => import('@/views/ai/DesignStudio.vue'),
    meta: { title: 'Design Studio', requiresAuth: true },
  },
  {
    path: '/recurring',
    name: 'RecurringPosts',
    component: () => import('@/views/recurring/RecurringList.vue'),
    meta: { title: 'Recurring Posts', requiresAuth: true },
  },
  {
    path: '/analytics',
    name: 'Analytics',
    component: () => import('@/views/analytics/Analytics.vue'),
    meta: { title: 'Analytics', requiresAuth: true },
  },
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('@/views/billing/Billing.vue'),
    meta: { title: 'Pricing & Billing', requiresAuth: true },
  },
  {
    path: '/storage',
    name: 'StorageDrive',
    component: () => import('@/views/storage/Drive.vue'),
    meta: { title: 'Storage', requiresAuth: true },
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/Dashboard.vue'),
    meta: { title: 'Admin', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/views/admin/Users.vue'),
    meta: { title: 'Admin Users', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/plans',
    name: 'AdminPlans',
    component: () => import('@/views/admin/Plans.vue'),
    meta: { title: 'Admin Plans', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/transactions',
    name: 'AdminTransactions',
    component: () => import('@/views/admin/Transactions.vue'),
    meta: { title: 'Admin Transactions', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/integrations',
    name: 'AdminIntegrations',
    component: () => import('@/views/admin/Integrations.vue'),
    meta: { title: 'Admin Integrations', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/app-review',
    name: 'AdminAppReview',
    component: () => import('@/views/admin/AppReviewComments.vue'),
    meta: { title: 'App Review', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/features',
    name: 'AdminFeatures',
    component: () => import('@/views/admin/Features.vue'),
    meta: { title: 'Feature Visibility', requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/canva-reports',
    name: 'AdminCanvaReports',
    component: () => import('@/views/admin/CanvaImportReports.vue'),
    meta: { title: 'Canva Import Reports', requiresAuth: true, requiresAdmin: true },
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to) {
    if (to.hash) return { selector: to.hash }
    return { x: 0, y: 0 }
  },
})

registerGuards(router)

export default router
